/**
 * 분기 재고 실사 보고서 — 보기·결재 모달 (v2.224.0 · 2026-10-01)
 *
 * ⭐숫자는 아무도 못 고친다 — 실사 기록에서 서버가 상신 시점에 굳혔다(확정본 PDF). 사람이 적는 것은 [특이사항]뿐.
 * ⭐품목 하나하나·사진은 여기 없다 — 재고관리 › 분기 실사 탭(사진은 NAS 에서 그때 읽는다) 또는 확정본 PDF(사진 없음).
 * 결재(승인·반려·회수·삭제)는 지출결재와 **같은 API** — 영업팀 월간보고서 모달과 같은 모양.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as api from '../lib/expense-api';
import { updateAuditReport, type StockAuditReportBody } from '../lib/stock-audit-api';
import { EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { useAuth } from '../lib/auth-context';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  docId: number;
  onClose: () => void;
}

function n(v: number | null | undefined): string {
  return v === null || v === undefined ? '-' : v.toLocaleString('ko-KR');
}

export function StockAuditReportModal({ docId, onClose }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const detail = useQuery({ queryKey: ['expense', 'doc', docId], queryFn: () => api.getExpenseDoc(docId) });
  const doc = detail.data?.doc ?? null;
  const body: StockAuditReportBody | null = (detail.data as { stockAudit?: StockAuditReportBody | null } | undefined)?.stockAudit ?? null;

  const [draft, setDraft] = useState<string | null>(null);
  const notes = draft ?? body?.notes ?? '';
  const canEditNotes = !!doc && !!user && doc.authorId === user.userId && (doc.status === '작성중' || doc.status === '반려');

  const save = useMutation({
    mutationFn: (submit: boolean) => updateAuditReport(docId, { notes, submit }),
    onSuccess: (_r, submit) => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['expense'] });
      qc.invalidateQueries({ queryKey: ['stock-audit'] });
      if (submit) onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const act = useMutation({
    mutationFn: (kind: 'approve' | 'reject' | 'recall') => {
      if (kind === 'approve') return api.approveExpenseDoc(docId);
      if (kind === 'recall') {
        if (doc?.status === '최종승인') {
          const r = window.prompt('완결(최종승인) 문서를 회수합니다.\n사유를 적어 주세요 (필수 — 처리 이력에 남습니다)') ?? '';
          if (!r.trim()) return Promise.reject(new Error('사유가 없으면 완결 문서를 회수할 수 없습니다'));
          return api.recallExpenseDoc(docId, r.trim());
        }
        return api.recallExpenseDoc(docId);
      }
      const reason = window.prompt('반려 사유를 적어 주세요 (선택)\n\n※ 반려되면 실사 담당자가 품목 판정·사진을 고쳐 다시 상신하며, 그때 숫자가 다시 굳습니다.') ?? '';
      return api.rejectExpenseDoc(docId, reason);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      qc.invalidateQueries({ queryKey: ['stock-audit'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const del = useMutation({
    mutationFn: () => api.deleteExpenseDoc(docId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense'] });
      qc.invalidateQueries({ queryKey: ['stock-audit'] });
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  const busy = save.isPending || act.isPending || del.isPending;
  const c = body?.counters;

  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-3xl my-6">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <h2 className="ui-modal-title shrink-0">분기 재고 실사 보고서</h2>
            {doc && <span className="text-xs text-slate-500 font-mono shrink-0">{doc.docNo}</span>}
            {doc && <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${EXPENSE_STATUS_BADGE[doc.status] ?? ''}`}>{doc.status}</span>}
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {detail.isLoading && <div className="text-sm text-slate-400 py-6 text-center"><LoadingLines /></div>}
          {doc?.rejectReason && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">반려 사유: {doc.rejectReason}</div>
          )}

          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">실사 개요</div>
              <table className="w-full text-[13px] border border-slate-200 rounded overflow-hidden">
                <tbody>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2 w-[130px]">대상 분기</th>
                    <td className="px-3 py-2"><b>{body.label}</b> <span className="text-slate-500 text-xs ml-1.5">({body.period.from} ~ {body.period.to})</span></td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">실사</th>
                    <td className="px-3 py-2">
                      {body.startedAt.slice(0, 16).replace('T', ' ')} {body.startedByName} 시작
                      {body.finishedAt && <span className="text-slate-500"> · 끝 {body.finishedAt.slice(0, 16).replace('T', ' ')}</span>}
                      <span className="text-slate-500 text-xs ml-1.5">실사자 {body.checkers.join(', ') || '-'}</span>
                    </td>
                  </tr>
                  <tr>
                    <th className="bg-slate-50 text-left font-medium text-slate-600 px-3 py-2">작성자</th>
                    <td className="px-3 py-2">{doc?.authorName} <span className="text-slate-500 text-xs ml-1.5">{doc?.authorDept} {doc?.authorTitle}</span></td>
                  </tr>
                </tbody>
              </table>
              {c && (
                <div className="grid grid-cols-5 gap-2 mt-2 text-center">
                  {([['대상', c.total, 'text-slate-900'], ['맞음', c.ok, 'text-emerald-700'], ['다름', c.diff, 'text-amber-700'], ['무시', c.skip, 'text-slate-600'], ['사진', body.photoTotal, 'text-slate-700']] as const).map(([l, v, cls]) => (
                    <div key={l} className="bg-slate-50 rounded py-1.5">
                      <div className={`text-lg font-bold tabular-nums ${cls}`}>{n(v)}</div>
                      <div className="text-xs text-slate-500">{l}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[12px] text-slate-600 mt-2">
                장부 합계 <b>{n(body.qty.systemTotal)}</b> · 실사 합계 <b>{n(body.qty.countedTotal)}</b>
                {' '}· 차이 <b className={body.qty.countedTotal - body.qty.systemTotal === 0 ? 'text-emerald-700' : 'text-rose-700'}>
                  {body.qty.countedTotal - body.qty.systemTotal > 0 ? '+' : ''}{n(body.qty.countedTotal - body.qty.systemTotal)}
                </b>
                <span className="text-slate-500 text-xs"> (맞음·다름 품목만 · 무시 제외)</span>
              </div>
              <p className="text-[12px] text-slate-500 mt-1.5">
                <b>숫자는 상신할 때 굳습니다</b> — 품목 하나하나는 [📄 보고서 보기] 확정본 PDF 나{' '}
                <Link to={`/stock?tab=audit&audit=${body.auditId}`} className="text-blue-600 underline" onClick={onClose}>재고관리 › 분기 실사</Link>에서(사진은 거기서만 봅니다 — PDF 에 싣지 않습니다).
              </p>
            </section>
          )}

          {body && body.diffs.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-amber-800 mb-1.5">⚠ 실제와 다른 품목 {body.diffs.length}종</div>
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium">품목</th>
                      <th className="text-right px-2 py-1.5 font-medium w-14">장부</th>
                      <th className="text-right px-2 py-1.5 font-medium w-14">실사</th>
                      <th className="text-right px-2 py-1.5 font-medium w-14">차이</th>
                      <th className="text-left px-2 py-1.5 font-medium">메모</th>
                      <th className="text-center px-2 py-1.5 font-medium w-12">사진</th>
                    </tr>
                  </thead>
                  <tbody>
                    {body.diffs.map((x) => (
                      <tr key={x.itemCode} className="border-t border-slate-100">
                        <td className="px-2 py-1.5"><div className="font-medium text-slate-900">{x.itemName ?? x.itemCode}</div><div className="text-[11px] text-slate-500">{x.itemCode}</div></td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{n(x.systemQty)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{x.countedQty === null ? <span className="text-slate-400">미기재</span> : n(x.countedQty)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-rose-700">{x.delta === null ? '-' : `${x.delta > 0 ? '+' : ''}${n(x.delta)}`}</td>
                        <td className="px-2 py-1.5 text-slate-700">{x.note ?? ''}</td>
                        <td className="px-2 py-1.5 text-center text-slate-500">{x.photoCount || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {body && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">특이사항{canEditNotes && <span className="font-normal text-slate-400 ml-1.5">— 작성자만 고칠 수 있습니다</span>}</div>
              {canEditNotes ? (
                <>
                  <textarea value={notes} onChange={(e) => setDraft(e.target.value)}
                    className="ui-input ui-textarea w-full min-h-[120px]" />
                  <div className="flex items-center gap-2 mt-1.5">
                    <button type="button" disabled={busy} onClick={() => save.mutate(false)}
                      className="btn btn-secondary">{save.isPending ? '저장 중…' : '💾 저장'}</button>
                    <button type="button" disabled={busy}
                      onClick={() => { if (window.confirm('결재로 다시 올립니다.\n\n※ 이때 실사 기록을 지금 상태로 다시 굳힙니다.')) save.mutate(true); }}
                      className="btn btn-primary">📤 다시 상신</button>
                    <span className="text-xs text-slate-500">품목 판정·사진은 모바일 실사 화면에서 고칩니다.</span>
                  </div>
                </>
              ) : (
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed border border-slate-200 rounded px-3 py-2 bg-slate-50/60 min-h-[52px]">
                  {body.notes || <span className="text-slate-300">적힌 내용이 없습니다</span>}
                </div>
              )}
            </section>
          )}

          {detail.data && detail.data.cells.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">결재 <span className="font-normal text-slate-400">— 경영지원 결재는 없습니다</span></div>
              <div className="flex gap-2">
                {detail.data.cells.map((cell) => (
                  <div key={cell.role} className="border border-slate-300 rounded text-center w-[86px]">
                    <div className="text-[11px] bg-slate-100 py-0.5 text-slate-600">{cell.role}</div>
                    <div className="h-9 flex items-center justify-center text-xs">
                      {cell.skipped ? <span className="text-slate-300">생략</span>
                        : cell.stamped ? (cell.userId && cell.hasSignature
                          ? <img src={signatureImageUrl(cell.userId)} alt={cell.name} className="max-h-[30px] max-w-[70px] object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          : <span className="font-semibold text-slate-700">{cell.name}</span>)
                          : <span className="text-slate-300">대기</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 pb-0.5">
                      {cell.skipped ? '' : (cell.stampedAt ?? '').slice(2, 10)}
                      {!cell.skipped && cell.delegated && <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <DocHistoryList history={detail.data?.history} />
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <div className="flex gap-2">
            {detail.data?.attachmentId ? (
              <button type="button"
                onClick={() => openServerPdfPreview({ pdfUrl: api.attachmentDownloadUrl(detail.data!.attachmentId!), filename: `${doc?.docNo ?? '분기재고실사보고서'}_분기재고실사보고서.pdf` })}
                className="btn btn-secondary">📄 보고서 보기</button>
            ) : (
              <span className="text-[12px] text-slate-400 self-center">확정본 PDF 는 상신할 때 만들어집니다.</span>
            )}
            {detail.data?.canDelete && (
              <button type="button" disabled={busy}
                onClick={() => { if (window.confirm(`${doc?.docNo ?? '이 문서'} 를 삭제할까요?\n그 분기 실사가 다시 열립니다(기록은 남습니다).`)) del.mutate(); }}
                className="btn btn-danger">{del.isPending ? '삭제 중…' : '🗑 삭제'}</button>
            )}
          </div>
          <div className="flex gap-2">
            {detail.data?.canRecall && (
              <button type="button" onClick={() => act.mutate('recall')} disabled={busy}
                className="btn btn-secondary">↩ 승인 회수</button>
            )}
            {detail.data?.canReject && (
              <button type="button" onClick={() => act.mutate('reject')} disabled={busy}
                className="btn btn-danger">반려</button>
            )}
            {detail.data?.canApprove && (
              <button type="button" onClick={() => act.mutate('approve')} disabled={busy}
                className="btn btn-success-solid">승인</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
